<template>
  <a-drawer title="Update Emergency Contact / อัปเดตข้อมูลผู้ติดต่อฉุกเฉิน" :width="720" :visible="visible"
    :body-style="{ paddingBottom: '80px' }" :closable="false" :maskClosable="false" @close="onClose"
    class="emergency-drawer font-web">
    <template slot="title">
      <div class="d-flex align-items-center font-web" style="display: flex; align-items: center;">
        <a-icon type="contacts" class="text-purple mr-10 f-18" />
        <span class="font-semibold f-16 font-web">Update Emergency Contact / อัปเดตข้อมูลผู้ติดต่อฉุกเฉิน</span>
      </div>
    </template>

    <div class="emergency-form-body font-web py-10">
      <!-- PDPA Notification Collapsible Box (Default Hidden) -->
      <div class="pdpa-collapsible-wrapper mb-20 font-web">
        <div class="pdpa-header-bar d-flex align-items-center justify-content-between font-web"
          style="display: flex; justify-content: space-between; align-items: center; width: 100%; padding: 12px 16px;"
          @click="isPdpaExpanded = !isPdpaExpanded" role="button" tabindex="0">
          <div class="d-flex align-items-center font-web" style="display: flex; align-items: center;">
            <a-icon type="safety-certificate" theme="twoTone" two-tone-color="#5832e5" class="mr-10 f-16" />
            <span class="font-weight-bold f-13 text-dark font-web">
              ข้อกำหนดการคุ้มครองข้อมูลส่วนบุคคล (PDPA Notice)
            </span>
          </div>
          <div class="d-flex align-items-center font-web" style="display: flex; align-items: center;">
            <a-icon :type="isPdpaExpanded ? 'up' : 'down'" class="f-14 text-purple" />
          </div>
        </div>

        <transition name="fade-slide">
          <div v-show="isPdpaExpanded" class="pdpa-expanded-content font-web" style="padding: 14px 16px;">
            <div class="font-web f-12 text-muted">
              วิทยาลัยนานาชาติ มหาวิทยาลัยมหิดล
              ขอความยินยอมในการจัดเก็บและใช้ข้อมูลการติดต่อส่วนตัวและข้อมูลผู้ติดต่อฉุกเฉินนี้
              เพื่อวัตถุประสงค์ในการดูแลความปลอดภัย การประสานงาน
              และการติดต่อในกรณีเกิดเหตุฉุกเฉินหรือมีความจำเป็นเร่งด่วนเท่านั้น
              ตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล (PDPA)
            </div>
          </div>
        </transition>
      </div>

      <!-- SECTION 1: Personal -->
      <div class="form-section-header mb-16 font-web">
        <span class="section-title text-purple font-semibold font-web">
          <a-icon type="user" class="mr-6" /> Personal :: ข้อมูลส่วนบุคคล
        </span>
      </div>

      <!-- Personal Address (Required) -->
      <div class="form-group mt-10 mb-20 font-web">
        <label class="form-label required font-web">
          Address ที่พักอาศัยปัจจุบัน <span class="text-danger">*</span>
        </label>
        <a-textarea v-model="form.personalAddress" :rows="3"
          placeholder="กรอกที่พักอาศัยปัจจุบัน (เช่น เลขที่ ซอย ถนน แขวง/ตำบล เขต/อำเภอ จังหวัด รหัสไปรษณีย์)"
          class="mt-6 font-web" />
        <div v-if="errors.personalAddress" class="text-danger f-12 mt-4 font-web">
          {{ errors.personalAddress }}
        </div>
      </div>

      <!-- Personal Phone (Required, 1 Digit per box) -->
      <div class="form-group mb-20 font-web">
        <label class="form-label required font-web">
          Phone number เบอร์โทรศัพท์ <span class="text-danger">*</span>
        </label>
        <div class="phone-digit-container mt-6 font-web">
          <div class="phone-digit-group d-flex align-items-center" style="display: inline-flex; align-items: center;">
            <!-- First 3 digits -->
            <input v-for="(digit, idx) in 3" :key="'pphone-' + idx" :ref="'personalPhone_' + idx"
              v-model="personalPhoneDigits[idx]" type="text" inputmode="numeric" maxlength="1"
              class="phone-digit-input text-center font-web" @input="onDigitInput($event, 'personalPhone', idx)"
              @keydown="onDigitKeydown($event, 'personalPhone', idx)" @paste="onDigitPaste($event, 'personalPhone')" />

            <span class="phone-digit-dash">-</span>

            <!-- Remaining 7 digits -->
            <input v-for="(digit, idx) in 7" :key="'pphone-' + (idx + 3)" :ref="'personalPhone_' + (idx + 3)"
              v-model="personalPhoneDigits[idx + 3]" type="text" inputmode="numeric" maxlength="1"
              class="phone-digit-input text-center font-web" @input="onDigitInput($event, 'personalPhone', idx + 3)"
              @keydown="onDigitKeydown($event, 'personalPhone', idx + 3)"
              @paste="onDigitPaste($event, 'personalPhone')" />
          </div>
        </div>
        <div v-if="errors.personalPhone" class="text-danger f-12 mt-4 font-web">
          {{ errors.personalPhone }}
        </div>
      </div>

      <!-- Personal Line (Optional) -->
      <div class="form-group mb-22 font-web">
        <label class="form-label font-web">
          Line contact (ถ้าสะดวกให้)
        </label>
        <a-input v-model="form.personalLine" placeholder="Line ID หรือหมายเลขโทรศัพท์ผูก Line" class="mt-6 font-web">
          <a-icon slot="prefix" type="message" class="text-muted" />
        </a-input>
      </div>

      <!-- SECTION 2: Emergency (Red Theme & Soft Red Background) -->
      <div class="emergency-section-box mt-20 mb-20 font-web">
        <div class="emergency-section-header mb-18 font-web">
          <span class="emergency-section-title font-semibold font-web">
            <a-icon type="alert" class="mr-8 text-danger" /> Emergency :: ข้อมูลติดต่อกรณีฉุกเฉิน
          </span>
        </div>

        <!-- Relation Options (Expanded select box) -->
        <div class="form-group mb-20 font-web">
          <label class="form-label required font-web">
            Relation ความสัมพันธ์ <span class="text-danger">*</span>
          </label>
          <a-select v-model="form.emergencyRelation" placeholder="-- เลือกความสัมพันธ์ --" size="large"
            class="w-100 mt-6 relation-select font-web">
            <a-select-option v-for="rel in relationOptions" :key="rel" :value="rel" class="font-web">
              {{ rel }}
            </a-select-option>
          </a-select>
          <div v-if="errors.emergencyRelation" class="text-danger f-12 mt-4 font-web">
            {{ errors.emergencyRelation }}
          </div>
        </div>

        <!-- Emergency Phone (Required, 1 Digit per box) -->
        <div class="form-group mb-20 font-web">
          <label class="form-label required font-web">
            Phone number เบอร์ติดต่อฉุกเฉิน <span class="text-danger">*</span>
          </label>
          <div class="phone-digit-container mt-6 font-web">
            <div class="phone-digit-group d-flex align-items-center" style="display: inline-flex; align-items: center;">
              <!-- First 3 digits -->
              <input v-for="(digit, idx) in 3" :key="'ephone-' + idx" :ref="'emergencyPhone_' + idx"
                v-model="emergencyPhoneDigits[idx]" type="text" inputmode="numeric" maxlength="1"
                class="phone-digit-input text-center font-web" @input="onDigitInput($event, 'emergencyPhone', idx)"
                @keydown="onDigitKeydown($event, 'emergencyPhone', idx)"
                @paste="onDigitPaste($event, 'emergencyPhone')" />

              <span class="phone-digit-dash">-</span>

              <!-- Remaining 7 digits -->
              <input v-for="(digit, idx) in 7" :key="'ephone-' + (idx + 3)" :ref="'emergencyPhone_' + (idx + 3)"
                v-model="emergencyPhoneDigits[idx + 3]" type="text" inputmode="numeric" maxlength="1"
                class="phone-digit-input text-center font-web" @input="onDigitInput($event, 'emergencyPhone', idx + 3)"
                @keydown="onDigitKeydown($event, 'emergencyPhone', idx + 3)"
                @paste="onDigitPaste($event, 'emergencyPhone')" />
            </div>
          </div>
          <div v-if="errors.emergencyPhone" class="text-danger f-12 mt-2 font-web">
            {{ errors.emergencyPhone }}
          </div>
        </div>

        <!-- Emergency Address (With Copy Current Address button) -->
        <div class="form-group mb-4 font-web">
          <div class="d-flex justify-content-between align-items-center mb-6 flex-wrap font-web"
            style="display: flex; justify-content: space-between; align-items: center; gap: 8px;">
            <label class="form-label mb-0 font-web">
              Address ที่พักอาศัยอื่นๆ กรณีเกิดเหตุฉุกเฉินที่จำเป็นต้องมีการติดต่อ :
            </label>
            <a-button type="dashed" size="small" class="btn-copy-address mb-10 font-web" @click="copyPersonalAddress">
              <a-icon type="copy" /> ใช้อันเดียวกับที่อยู่ปัจจุบัน
            </a-button>
          </div>
          <a-textarea v-model="form.emergencyAddress" :rows="3"
            placeholder="กรอกที่พักอาศัยกรณีเกิดเหตุฉุกเฉิน หรือกดปุ่มด้านบนเพื่อใช้อันเดียวกับที่พักปัจจุบัน"
            class="mt-6 font-web" />
        </div>
      </div>
    </div>

    <!-- Drawer Footer (Sticky at bottom as per Ant Design pattern) -->
    <div :style="{
      position: 'absolute',
      right: 0,
      bottom: 0,
      width: '100%',
      borderTop: '1px solid #e9e9e9',
      padding: '10px 16px',
      background: '#fff',
      textAlign: 'right',
      zIndex: 1,
    }" class="drawer-footer font-web">
      <a-button :style="{ marginRight: '8px' }" class="btn-close-modal font-web" @click="onClose">
        Close
      </a-button>
      <a-button type="primary" class="btn-update-emergency font-web" :loading="isSubmitting" @click="handleSubmitForm">
        Save
      </a-button>
    </div>
  </a-drawer>
</template>

<script>
export default {
  name: 'DrawerEmergencyContact',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: () => ({}),
    },
    dataBaseProfile: {
      type: [Object, Array],
      default: () => ({}),
    },
    dataProfileManage: {
      type: [Object, Array],
      default: () => ({}),
    },
  },
  computed: {
    userCode() {
      // 1. ดึงจาก dataBaseProfile (จาก getBaseProfile)
      if (this.dataBaseProfile) {
        if (Array.isArray(this.dataBaseProfile)) {
          if (this.dataBaseProfile[0] && this.dataBaseProfile[0].code) {
            return this.dataBaseProfile[0].code;
          }
        } else if (this.dataBaseProfile.code) {
          return this.dataBaseProfile.code;
        }
      }
      // 2. Fallback จาก initialData.code
      if (this.initialData && this.initialData.code) {
        return this.initialData.code;
      }
      // 3. Fallback จาก dataProfileManage
      if (this.dataProfileManage) {
        if (Array.isArray(this.dataProfileManage)) {
          if (this.dataProfileManage[0] && this.dataProfileManage[0].code) {
            return this.dataProfileManage[0].code;
          }
        } else if (this.dataProfileManage.code) {
          return this.dataProfileManage.code;
        }
      }
      return '';
    },
    manageCode() {
      return this.userCode;
    },
  },
  data() {
    return {
      isSubmitting: false,
      isPdpaExpanded: false,
      relationOptions: ['พ่อ', 'แม่', 'พี่', 'น้อง', 'ลุง', 'ป้า', 'ญาติคนสนิท'],
      personalPhoneDigits: Array(10).fill(''),
      emergencyPhoneDigits: Array(10).fill(''),
      form: {
        personalAddress: '',
        personalPhone: '',
        personalLine: '',
        emergencyRelation: undefined,
        emergencyAddress: '',
        emergencyPhone: '',
      },
      errors: {},
    };
  },
  watch: {
    visible(val) {
      if (val) {
        this.initFormData();
      }
    },
    initialData: {
      deep: true,
      handler() {
        if (this.visible) {
          this.initFormData();
        }
      },
    },
  },
  created() {
    this.initFormData();
  },
  methods: {
    initFormData() {
      const data = this.initialData || {};
      this.form = {
        personalAddress: data.personalAddress || '',
        personalPhone: data.personalPhone || '',
        personalLine: data.personalLine || '',
        emergencyRelation: data.emergencyRelation || undefined,
        emergencyAddress: data.emergencyAddress || '',
        emergencyPhone: data.emergencyPhone || '',
      };
      this.errors = {};
      this.isPdpaExpanded = false;

      // Personal phone number default
      const pRaw = (data.personalPhone || '').replace(/\D/g, '').slice(0, 10);
      this.personalPhoneDigits = Array(10).fill('');
      for (let i = 0; i < pRaw.length; i++) {
        this.$set(this.personalPhoneDigits, i, pRaw[i]);
      }
      if (pRaw) {
        this.form.personalPhone = this.formatThaiPhone(pRaw);
      }

      // Emergency phone number default
      const eRaw = (data.emergencyPhone || '').replace(/\D/g, '').slice(0, 10);
      this.emergencyPhoneDigits = Array(10).fill('');
      for (let i = 0; i < eRaw.length; i++) {
        this.$set(this.emergencyPhoneDigits, i, eRaw[i]);
      }
      if (eRaw) {
        this.form.emergencyPhone = this.formatThaiPhone(eRaw);
      }
    },
    onClose() {
      this.$emit('close');
      this.$emit('update:visible', false);
    },
    onDigitInput(e, type, idx) {
      const val = (e && e.target ? e.target.value : e || '').replace(/\D/g, '');
      if (val.length > 1) {
        this.handleMultipleDigits(type, idx, val);
        return;
      }
      const digit = val.slice(0, 1);
      const digitsArr = type === 'personalPhone' ? this.personalPhoneDigits : this.emergencyPhoneDigits;
      this.$set(digitsArr, idx, digit);
      this.syncDigitsToPhone(type);

      if (digit && idx < 9) {
        this.focusDigitInput(type, idx + 1);
      }
    },
    onDigitKeydown(e, type, idx) {
      const digitsArr = type === 'personalPhone' ? this.personalPhoneDigits : this.emergencyPhoneDigits;
      if (e.key === 'Backspace') {
        if (!digitsArr[idx] && idx > 0) {
          e.preventDefault();
          this.$set(digitsArr, idx - 1, '');
          this.syncDigitsToPhone(type);
          this.focusDigitInput(type, idx - 1);
        } else {
          this.$set(digitsArr, idx, '');
          this.syncDigitsToPhone(type);
        }
      } else if (e.key === 'ArrowLeft' && idx > 0) {
        e.preventDefault();
        this.focusDigitInput(type, idx - 1);
      } else if (e.key === 'ArrowRight' && idx < 9) {
        e.preventDefault();
        this.focusDigitInput(type, idx + 1);
      }
    },
    onDigitPaste(e, type) {
      e.preventDefault();
      const clipboardData = e.clipboardData || window.clipboardData;
      const text = clipboardData ? clipboardData.getData('text') : '';
      const numbers = text.replace(/\D/g, '');
      if (numbers) {
        this.handleMultipleDigits(type, 0, numbers);
      }
    },
    handleMultipleDigits(type, startIdx, numbers) {
      const digitsArr = type === 'personalPhone' ? this.personalPhoneDigits : this.emergencyPhoneDigits;
      const chars = numbers.slice(0, 10 - startIdx);
      for (let i = 0; i < chars.length; i++) {
        this.$set(digitsArr, startIdx + i, chars[i]);
      }
      this.syncDigitsToPhone(type);
      const nextIdx = Math.min(startIdx + chars.length, 9);
      this.focusDigitInput(type, nextIdx);
    },
    syncDigitsToPhone(type) {
      const digitsArr = type === 'personalPhone' ? this.personalPhoneDigits : this.emergencyPhoneDigits;
      const full = digitsArr.join('');
      this.form[type] = this.formatThaiPhone(full);
      if (this.errors[type]) {
        this.validateField(type);
      }
    },
    focusDigitInput(type, idx) {
      this.$nextTick(() => {
        const refName = `${type}_${idx}`;
        const target = this.$refs[refName];
        if (target) {
          const inputEl = Array.isArray(target) ? target[0] : target;
          if (inputEl && typeof inputEl.focus === 'function') {
            inputEl.focus();
            if (typeof inputEl.select === 'function') {
              inputEl.select();
            }
          }
        }
      });
    },
    formatThaiPhone(val) {
      if (!val) return '';
      const numbers = String(val).replace(/\D/g, '').slice(0, 10);
      if (numbers.length <= 3) {
        return numbers;
      }
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    },
    isValidThaiPhone(phone) {
      if (!phone) return false;
      const raw = phone.replace(/\D/g, '');
      return raw.length === 10 && raw.startsWith('0');
    },
    copyPersonalAddress() {
      if (this.form.personalAddress && this.form.personalAddress.trim() !== '') {
        this.form.emergencyAddress = this.form.personalAddress.trim();
        this.$message.success('คัดลอกที่พักอาศัยปัจจุบันไปยังข้อมูลติดต่อฉุกเฉินแล้ว');
      } else {
        this.$message.warning('กรุณากรอกที่พักอาศัยปัจจุบันก่อน');
      }
    },
    validateField(field) {
      if (field === 'personalAddress') {
        if (!this.form.personalAddress || this.form.personalAddress.trim() === '') {
          this.$set(this.errors, 'personalAddress', 'กรุณาระบุที่พักอาศัยปัจจุบัน');
        } else {
          this.$delete(this.errors, 'personalAddress');
        }
      }
      if (field === 'personalPhone') {
        if (!this.form.personalPhone) {
          this.$set(this.errors, 'personalPhone', 'กรุณาระบุหมายเลขโทรศัพท์ 10 หลัก');
        } else if (!this.isValidThaiPhone(this.form.personalPhone)) {
          this.$set(this.errors, 'personalPhone', 'รูปแบบเบอร์โทรศัพท์ไม่ถูกต้อง (ต้องขึ้นต้นด้วย 0 และมีครบ 10 หลัก)');
        } else {
          this.$delete(this.errors, 'personalPhone');
        }
      }
      if (field === 'emergencyRelation') {
        if (!this.form.emergencyRelation) {
          this.$set(this.errors, 'emergencyRelation', 'กรุณาเลือกความสัมพันธ์กับผู้ติดต่อฉุกเฉิน');
        } else {
          this.$delete(this.errors, 'emergencyRelation');
        }
      }
      if (field === 'emergencyPhone') {
        if (!this.form.emergencyPhone) {
          this.$set(this.errors, 'emergencyPhone', 'กรุณาระบุหมายเลขโทรศัพท์ติดต่อฉุกเฉิน 10 หลัก');
        } else if (!this.isValidThaiPhone(this.form.emergencyPhone)) {
          this.$set(this.errors, 'emergencyPhone', 'รูปแบบเบอร์โทรศัพท์ไม่ถูกต้อง (ต้องขึ้นต้นด้วย 0 และมีครบ 10 หลัก)');
        } else {
          this.$delete(this.errors, 'emergencyPhone');
        }
      }
    },
    validateAll() {
      this.validateField('personalAddress');
      this.validateField('personalPhone');
      this.validateField('emergencyRelation');
      this.validateField('emergencyPhone');
      return Object.keys(this.errors).length === 0;
    },
    async handleSubmitForm() {
      if (!this.validateAll()) {
        this.$message.error('กรุณากรอกข้อมูลที่จำเป็นให้ถูกต้องครบถ้วน');
        return;
      }
      await this.patchEmergency();
    },
    async patchEmergency() {
      const code = this.userCode;
      if (!code) {
        this.$message.error('ไม่พบรหัสบุคลากร (Staff Code)');
        return;
      }

      this.isSubmitting = true;
      try {
        const sendform = new FormData();
        sendform.append('adress', this.form.personalAddress || '');
        sendform.append('emergency_call', this.form.personalPhone || '');

        const emergencyAddress = this.form.emergencyAddress ? this.form.emergencyAddress.trim() : '';
        const emergencyNameFormatted = `${this.form.emergencyRelation || ''}#${this.form.emergencyPhone || ''}#${emergencyAddress}`;
        sendform.append('emergency_name', emergencyNameFormatted);

        const response = await this.$http.patch(
          `/staff/profile/${code}/`,
          sendform,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );

        if (response && (response.status === 200 || response.status === 201 || response.status === 204)) {
          this.$message.success('อัปเดตข้อมูลผู้ติดต่อฉุกเฉินเรียบร้อยแล้ว');
          const resData = response.data || {
            personalAddress: this.form.personalAddress,
            personalPhone: this.form.personalPhone,
            personalLine: this.form.personalLine,
            emergencyRelation: this.form.emergencyRelation,
            emergencyAddress: this.form.emergencyAddress,
            emergencyPhone: this.form.emergencyPhone,
          };

          // อัปเดตใน dataBaseProfile เดิม (ถ้ามี)
          if (this.dataBaseProfile) {
            if (Array.isArray(this.dataBaseProfile) && this.dataBaseProfile.length > 0) {
              Object.assign(this.dataBaseProfile[0], resData);
            } else if (typeof this.dataBaseProfile === 'object') {
              Object.assign(this.dataBaseProfile, resData);
            }
          }

          this.$emit('save', resData);
          this.onClose();
          return response;
        } else {
          const error = new Error('Request Failed');
          error.response = response;
          throw error;
        }
      } catch (error) {
        if (this.$notification) {
          this.$notification.open({
            style: {
              backgroundColor: '#ff1919',
              color: '#ffffff',
            },
            message: function (h) {
              return h('div', [h('strong', { class: '' }, 'Error')]);
            },
            description: `Problem issue connect api : /staff/profile/${code}/`,
          });
        }
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* Flex utilities */
.d-flex {
  display: flex !important;
}

.align-items-center {
  align-items: center !important;
}

.justify-content-between {
  justify-content: space-between !important;
}

.justify-content-end {
  justify-content: flex-end !important;
}

.w-100 {
  width: 100% !important;
}

.text-purple {
  color: #5832e5;
}

.btn-update-emergency {
  background-color: #5832e5;
  border-color: #5832e5;
  color: #ffffff;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
}

.btn-update-emergency:hover,
.btn-update-emergency:focus,
.btn-update-emergency:active {
  background-color: #4825d4;
  border-color: #4825d4;
  color: #ffffff;
}

.form-label {
  font-weight: 600;
  font-size: 13.5px;
  color: #262626;
  margin-bottom: 6px;
  line-height: 1.6;
  display: inline-block;
}

.form-section-header {
  border-left: 3px solid #5832e5;
  padding-left: 10px;
  line-height: 1.6;
}

.section-title {
  font-size: 15px;
  line-height: 1.6;
  letter-spacing: 0.2px;
}

/* Emergency Box with Soft Red Background */
.emergency-section-box {
  background-color: #fff1f0;
  border: 1px solid #ffa39e;
  border-left: 4px solid #f5222d;
  border-radius: 10px;
  padding: 22px 24px;
  line-height: 1.6;
}

.emergency-section-header {
  padding-bottom: 10px;
  border-bottom: 1px dashed #ffccc7;
  line-height: 1.6;
}

.emergency-section-title {
  color: #cf1322;
  font-size: 15px;
  line-height: 1.5;
  letter-spacing: 0.2px;
}

.emergency-section-box .form-label {
  color: #333333;
  line-height: 1.6;
  margin-bottom: 6px;
  font-size: 13.5px;
}

.relation-select {
  width: 100%;
}

.btn-copy-address {
  color: #cf1322;
  border-color: #ffa39e;
  background-color: #ffffff;
  font-size: 12px;
  height: 28px;
  line-height: 26px;
  padding: 0 10px;
  border-radius: 4px;
  transition: all 0.2s ease-in-out;
}

.btn-copy-address:hover {
  color: #a8071a;
  border-color: #f5222d;
  background-color: #fff5f5;
}

/* Phone Digit Inputs (1 digit per box) */
.phone-digit-container {
  overflow-x: auto;
  padding: 2px 0;
}

.phone-digit-group {
  display: inline-flex;
  align-items: center;
}

.phone-digit-input {
  width: 36px;
  height: 40px;
  margin-right: 5px;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  background-color: #ffffff;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  outline: none;
  transition: all 0.2s ease-in-out;
}

.phone-digit-input:focus {
  border-color: #5832e5;
  box-shadow: 0 0 0 2px rgba(88, 50, 229, 0.2);
}

.emergency-section-box .phone-digit-input:focus {
  border-color: #f5222d;
  box-shadow: 0 0 0 2px rgba(245, 34, 45, 0.2);
}

.phone-digit-dash {
  font-size: 18px;
  font-weight: 700;
  color: #8c8c8c;
  line-height: 1;
  padding: 0 6px;
  margin-right: 5px;
}

/* PDPA Collapsible Notice */
.pdpa-collapsible-wrapper {
  border: 1px solid #eef1f8;
  border-radius: 8px;
  background-color: #fafbfc;
  overflow: hidden;
}

.pdpa-header-bar {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  width: 100% !important;
  padding: 12px 16px;
  cursor: pointer;
  background-color: #f4f6fc;
  transition: background-color 0.2s ease-in-out;
  user-select: none;
}

.pdpa-header-bar:hover {
  background-color: #eaeffa;
}

.pdpa-expanded-content {
  border-top: 1px dashed #d9e1f2;
  background-color: #ffffff;
  padding: 14px 16px;
  line-height: 1.7;
}

.btn-close-modal {
  border-radius: 6px;
}

/* Ensure font-web (Sarabun) is applied to all elements inside emergency-drawer */
.emergency-drawer,
.emergency-drawer /deep/ .ant-drawer,
.emergency-drawer /deep/ .ant-drawer-content,
.emergency-drawer /deep/ .ant-drawer-header,
.emergency-drawer /deep/ .ant-drawer-title,
.emergency-drawer /deep/ .ant-drawer-body,
.emergency-drawer /deep/ input,
.emergency-drawer /deep/ textarea,
.emergency-drawer /deep/ button,
.emergency-drawer /deep/ .ant-select,
.emergency-drawer /deep/ .ant-select-selection,
.emergency-drawer /deep/ .ant-select-dropdown,
.emergency-drawer /deep/ .ant-btn {
  font-family: 'Sarabun', sans-serif !important;
}
</style>
